import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PersonAdd,
  Email,
  CheckCircle,
  MoreVert,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

export function MemberCard({ member }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [requestStatus, setRequestStatus] = useState(null);

  const sendFriendRequest = async (toUserId) => {
    const token = localStorage.getItem('jwttoken');
    if (!token) {
      Swal.fire('Error', 'User not authenticated', 'error');
      return;
    }

    try {
      const response = await axios.post(
        'https://tdtlworld.com/dss-kaas-backend/socialapp/friend_requests/send/',
        { to_user_id: toUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setRequestStatus('pending');
      Swal.fire({
        title: 'Success',
        text: response.data.detail,
        icon: 'success',
        confirmButtonColor: '#D4AF37',
      });
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setRequestStatus('pending');
        Swal.fire({
          title: 'Info',
          text: error.response.data.detail,
          icon: 'info',
          confirmButtonColor: '#D4AF37',
        });
      } else {
        Swal.fire('Error', 'Failed to send friend request', 'error');
      }
    }
  };

  const profilePic = member.profile_pic && member.profile_pic.trim() !== '' ? member.profile_pic : null;

  const handleMessageClick = (recipient) => {
    navigate(`/textmessage/${recipient.user_id}`, {
      state: { name: recipient.full_name, user_id: recipient.user_id },
    });
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '20px',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
          borderColor: '#D4AF37',
        },
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ textAlign: 'center', pt: 4, pb: 4 }}>
        {/* Avatar Section */}
        <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
          <Avatar
            src={profilePic}
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              border: '4px solid',
              borderColor: 'background.paper',
              boxShadow: theme.shadows[2],
              fontSize: '2rem',
              fontWeight: 800,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            {!profilePic && (
              <>
                {member.first_name.charAt(0)}
                {member.last_name.charAt(0)}
              </>
            )}
          </Avatar>
        </Box>

        {/* Member Name */}
        <Typography variant="h6" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.5px' }}>
          {member.first_name} {member.last_name}
        </Typography>

        {/* Member Details */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Joined <strong>{member.joined_date}</strong><br />
          Last Active: <strong>{member.last_active}</strong>
        </Typography>

        {/* Action Buttons */}
        <Stack spacing={1.5}>
          {member.is_friends ? (
            <Button
              fullWidth
              variant="contained"
              disabled
              startIcon={<CheckCircle />}
              sx={{
                borderRadius: '50px',
                bgcolor: 'success.main',
                '&.Mui-disabled': { bgcolor: alpha(theme.palette.success.main, 0.15), color: 'success.main' }
              }}
            >
              Connected
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => sendFriendRequest(member.user_id)}
              disabled={requestStatus === 'pending'}
              sx={{
                borderRadius: '50px',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(212, 175, 55, 0.3)'
              }}
            >
              {requestStatus === 'pending' ? 'Request Sent' : 'Add Friend'}
            </Button>
          )}

          <Button
            fullWidth
            variant="outlined"
            startIcon={<Email />}
            onClick={() => handleMessageClick(member)}
            sx={{
              borderRadius: '50px',
              fontWeight: 700,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 }
            }}
          >
            Message
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
